import { useId } from 'react';
import { useConfig } from '../context/ConfigContext';
import { formatTimeSlotRangeMeridiem, getCourtChipColor, getCourtLabel } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { formatHourCount } from '../utils/selectedSlotsUtil';

/**
 * One court's selected hours in the guest summary: a header in the court's
 * colour with its name and hour count, then a row per hour — the time on a
 * tinted pill, the price, and × to remove that hour.
 *
 * `collapsible` (the stacked phone layout) turns the header into a toggle
 * that hides the hour rows; the hour count stays in the header either way,
 * so a collapsed card still says what it holds.
 *
 * @param {object} props
 * @param {number} props.court - court number
 * @param {Array<{dateStr: string, timeSlot: string, court: number, price: number}>} props.rows - this court's hours
 * @param {(row: object) => void} props.onRemove - deselects one hour
 * @param {boolean} [props.collapsible]
 * @param {boolean} [props.expanded] - only read when collapsible
 * @param {() => void} [props.onToggle] - only used when collapsible
 * @returns {JSX.Element}
 */
function SelectedCourtCard({ court, rows, onRemove, collapsible = false, expanded = true, onToggle }) {
  const { config } = useConfig();
  const bodyId = useId();
  const label = getCourtLabel(config, court);
  const color = getCourtChipColor(court);
  const open = !collapsible || expanded;
  const hourCount = formatHourCount(rows.length);

  const headerContent = (
    <>
      <span className="ssc-name" title={label}>{label}</span>
      <span className="ssc-count">{hourCount}</span>
    </>
  );

  return (
    <div
      className={`ssc-card${open ? ' ssc-open' : ''}`}
      style={{ '--ssc-accent': color.accent, '--ssc-tint': color.tint }}
    >
      {collapsible ? (
        <button
          type="button"
          className="ssc-head ssc-head-toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          {headerContent}
          <span className="ssc-chev" aria-hidden="true" />
        </button>
      ) : (
        <div className="ssc-head">{headerContent}</div>
      )}

      {open && (
        <div className="ssc-body" id={bodyId}>
          {rows.map((row) => {
            const time = formatTimeSlotRangeMeridiem(row.timeSlot);
            return (
              <div className="ssc-row" key={`${row.dateStr}__${row.timeSlot}__${row.court}`}>
                <span className="ssc-time">{time}</span>
                <b className="ssc-fee">{formatMoney(row.price, config)}</b>
                <button
                  type="button"
                  className="ssc-remove"
                  aria-label={`Remove ${label}, ${time}`}
                  onClick={() => onRemove(row)}
                >&times;</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SelectedCourtCard;
